import React, { memo, useCallback, useMemo } from 'react';
import './DetailCard.css';

// ─── Get icon component by type ───
const getIconByType = (type) => {
  if (type === 'pdf')
    return (
      <img
        src={process.env.PUBLIC_URL + '/assests/img/newPdf.svg'}
        alt="PDF"
        className="detail-card__file-icon"
      />
    );
  if (type === 'doc' || type === 'docx')
    return (
      <img
        src={process.env.PUBLIC_URL + '/assests/img/newDocment.svg'}
        alt="DOC"
        className="detail-card__file-icon"
      />
    );
  return type; // custom JSX icon passed directly
};

// ─── Reusable Field Row ───
const FieldRow = memo(
  ({ icon, label, value, bold, boldValue, separator = ' :' }) => {
    if (!value && value !== 0) return null;

    const fieldClass = `detail-card__field${bold ? ' detail-card__field--bold' : ''}${boldValue ? ' detail-card__field--bold-value' : ''}`;

    return (
      <div className={fieldClass}>
        {icon && (
          <span className="detail-card__field-icon" aria-hidden="true">
            {typeof icon === 'string' ? getIconByType(icon) : icon}
          </span>
        )}
        {label && (
          <>
            <span className="detail-card__field-label">{label}</span>
            <span className="detail-card__field-separator">{separator}</span>
          </>
        )}
        <span className="detail-card__field-value" title={String(value)}>
          {value}
        </span>
      </div>
    );
  }
);

FieldRow.displayName = 'FieldRow';

// ─── Footer Action Button ───
const ActionButton = memo(({ icon, label, tooltip, onClick }) => (
  <button
    type="button"
    className={`detail-card__footer-action ${label ? 'detail-card__footer-action--labeled' : ''}`}
    title={tooltip}
    aria-label={tooltip}
    style={!onClick ? { cursor: 'default' } : undefined}
    onClick={(e) => {
      e.stopPropagation();
      onClick?.(e);
    }}
  >
    {icon}
    {label && <span className="detail-card__footer-action-label">{label}</span>}
  </button>
));

ActionButton.displayName = 'ActionButton';

/**
 * DetailCard — A production-ready, reusable card component.
 *
 * @param {string}   headerTitle   - Text displayed in the yellow ribbon header
 * @param {number}   [badge]       - Optional circular badge value (e.g. match %)
 * @param {string}   [badgeSuffix] - Suffix after badge number (default: "%")
 * @param {Array}    bodyFields    - Array of { icon?, label?, value, bold?, separator? }
 * @param {Array}    [footerActions] - Array of { icon, tooltip, onClick }
 * @param {function} [onClick]     - Card click handler
 * @param {string}   [className]   - Additional CSS class
 * @param {string}   [testId]      - data-testid for testing
 */
const DetailCard = ({
  headerTitle,
  badge,
  badgeSuffix = '%',
  bodyFields = [],
  footerActions = [],
  onClick,
  className = '',
  testId,
}) => {
  // memoize fields to avoid unnecessary re-renders
  const renderedFields = useMemo(
    () =>
      bodyFields.map((field, idx) => (
        <FieldRow
          key={field.key || idx}
          icon={field.icon}
          label={field.label}
          value={field.value}
          bold={field.bold}
          boldValue={field.boldValue}
          separator={field.separator}
        />
      )),
    [bodyFields]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (onClick && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        onClick(e);
      }
    },
    [onClick]
  );

  const hasFooter = footerActions.length > 0;

  return (
    <div
      className={`detail-card ${className}`}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={headerTitle}
      data-testid={testId}
    >
      {/* ── Header Ribbon ── */}
      <div className="detail-card__header">
        <span className="detail-card__header-title" title={headerTitle}>
          {headerTitle}
        </span>
      </div>

      {/* ── Badge ── */}
      {badge != null && (
        <div
          className="detail-card__badge"
          aria-label={`${badge}${badge === 'N/A' ? '' : badgeSuffix}`}
        >
          {badge}
          {badge === 'N/A' ? '' : badgeSuffix}
        </div>
      )}

      {/* ── Body ── */}
      <div className="detail-card__body">{renderedFields}</div>

      {/* ── Footer Actions ── */}
      {hasFooter &&
        (() => {
          const labeled = footerActions.filter((a) => a.label);
          const iconOnly = footerActions.filter((a) => !a.label);
          return (
            <div
              className="detail-card__footer"
              role="toolbar"
              aria-label="Actions"
            >
              {labeled.map((action, idx) => (
                <ActionButton
                  key={action.key || `l-${idx}`}
                  icon={action.icon}
                  label={action.label}
                  tooltip={action.tooltip}
                  onClick={action.onClick}
                />
              ))}
              {labeled.length > 0 && iconOnly.length > 0 && (
                <div className="detail-card__footer-spacer" />
              )}
              {iconOnly.map((action, idx) => (
                <ActionButton
                  key={action.key || `i-${idx}`}
                  icon={action.icon}
                  tooltip={action.tooltip}
                  onClick={action.onClick}
                />
              ))}
            </div>
          );
        })()}
    </div>
  );
};

// ─── Memoized Export ───
export default memo(DetailCard);
